import type { IAccount, IJwt, IProfileForm } from '../types'
import type { VerificationTypes } from '~/shared/api'
import {
  useConfirmVerificationCodeMutation,
  useRequestPasswordResetMutation,
  useSendVerificationCodeMutation,
  useVerifyPasswordResetOtpMutation,
} from '~/shared/api'
import { REGEX_FOR_CHECK_EMAIL } from '~/shared/const'
import { decodeJwt } from '~/shared/lib'
import { AuthMethod, METHOD_EXPIRES_IN } from '../const'

export const useAccountStore = defineStore(
  'account',
  () => {
    const route = useRoute()
    const { mutate: requestPasswordResetOtpMutation }
      = useRequestPasswordResetMutation()
    const { mutate: sendVerificationCodeMutation }
      = useSendVerificationCodeMutation()
    const { mutate: verifyPasswordResetOtpMutation }
      = useVerifyPasswordResetOtpMutation()
    const { mutate: verifyRegistrationMutation }
      = useConfirmVerificationCodeMutation()
    const methodExpiresIn = useCookie<number | undefined>('sms_expires_in', {
      secure: true,
      httpOnly: false,
    })

    const verifiedResetCode = ref(false)
    const verifiedRegisterCode = ref(false)

    const data = ref<IAccount>({
      id: '',
      email: '',
      phone: '',
      lastName: '',
      firstName: '',
      middleName: '',
      birthday: '',
      emailVerified: false,
      preferredUsername: '',
      roles: [],
    })

    function getRequiredContactField() {
      const method = useRoute().query.method as AuthMethod
      if (method === AuthMethod.phone && data.value.phone) {
        return AuthMethod.email
      }

      if (method === AuthMethod.email && data.value.email) {
        return AuthMethod.phone
      }

      return undefined
    }

    const isQuestAdmin = computed(() => data.value.roles.includes('quest-admin'))

    const phone = computed(() => data.value.phone)

    const phoneOrEmail = computed({
      get: () => {
        if (route.query.method) {
          return route.query.method === AuthMethod.phone
            ? data.value.phone
            : data.value.email
        }

        if (data.value.phone) {
          return data.value.phone
        }

        return data.value.email
      },
      set: (value: string) => {
        if (REGEX_FOR_CHECK_EMAIL.test(value)) {
          data.value.phone = ''
          data.value.email = value
          return
        }

        data.value.phone = value
        data.value.email = ''
      },
    })

    function setProfileForm(form: IProfileForm) {
      data.value.lastName = form.lastName
      data.value.middleName = form.middleName
      data.value.firstName = form.firstName
      data.value.birthday = form.birthday
      const requiredContactField = getRequiredContactField()

      if (requiredContactField) {
        data.value[requiredContactField] = form.phoneOrEmail
      }
    }

    function setAccountFromJwt(jwt?: string | null) {
      if (!jwt) {
        return
      }

      const result = decodeJwt<IJwt>(jwt)

      if (result) {
        data.value.roles = result.realm_access.roles

        if (result.sub) {
          data.value.id = result.sub
        }
      }
    }

    function resetAllExceptIdentifier() {
      data.value.lastName = ''
      data.value.firstName = ''
      data.value.middleName = ''
      data.value.birthday = ''
      data.value.emailVerified = false
      data.value.preferredUsername = ''
      data.value.roles = []
    }

    function resetIdentifier() {
      data.value.phone = ''
      data.value.email = ''
    }

    function reset() {
      resetAllExceptIdentifier()
      resetIdentifier()
      data.value.id = ''
    }

    async function verifyRegistrationOtp(
      otpCode: string,
      type: VerificationTypes,
    ) {
      await verifyRegistrationMutation({
        input: {
          identifier: phoneOrEmail.value,
          code: otpCode,
          type,
        },
      })
      methodExpiresIn.value = undefined
      verifiedRegisterCode.value = true
    }

    async function verifyPasswordResetOtp(otpCode: string) {
      await verifyPasswordResetOtpMutation({
        identifier: phoneOrEmail.value,
        otpCode,
      })
      methodExpiresIn.value = undefined
      verifiedResetCode.value = true
    }

    function requestRegistrationOtp(type: VerificationTypes) {
      if (!methodExpiresIn.value) {
        sendVerificationCodeMutation({
          input: {
            identifier: phoneOrEmail.value,
            type,
          },
        })
      }
      setMethodExpiresIn()
    }

    function requestPasswordResetOtp() {
      if (!methodExpiresIn.value) {
        const method = REGEX_FOR_CHECK_EMAIL.test(phoneOrEmail.value)
          ? 'email'
          : 'sms'
        requestPasswordResetOtpMutation({
          identifier: phoneOrEmail.value,
          method,
        })
      }
      setMethodExpiresIn()
    }

    async function setMethodExpiresIn() {
      if (!methodExpiresIn.value) {
        methodExpiresIn.value = Date.now() + METHOD_EXPIRES_IN * 1000
      }
      else {
        const currentTime = Date.now()
        const timeout = setTimeout(() => {
          methodExpiresIn.value = undefined
          clearTimeout(timeout)
        }, methodExpiresIn.value - currentTime)
      }
    }

    return {
      data,
      isQuestAdmin,
      verifiedResetCode,
      verifiedRegisterCode,
      methodExpiresIn,
      phone,
      phoneOrEmail,
      getRequiredContactField,
      setProfileForm,
      setAccountFromJwt,
      resetAllExceptIdentifier,
      resetIdentifier,
      reset,
      verifyRegistrationOtp,
      verifyPasswordResetOtp,
      requestRegistrationOtp,
      requestPasswordResetOtp,
    }
  },
  {
    persist: {
      storage: piniaPluginPersistedstate.cookies({
        httpOnly: false,
        sameSite: 'strict',
      }),
    },
  },
)
